import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Camera, Video, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MINIMUM_DISCOUNT_ORDER = 200;

const PACKAGE_DETAILS = {
  starter: { name: 'Starter', description: 'Perfect for intimate events', highlight: false, deliveryTime: '10-30 days' },
  classic: { name: 'Classic', description: 'Our most popular choice', highlight: true, deliveryTime: '10-30 days' },
  elite: { name: 'Elite', description: 'The complete premium experience', highlight: false, deliveryTime: '10-30 days' },
};

const Packages = () => {
  const [events, setEvents] = useState([]);
  const [servicesPhoto, setServicesPhoto] = useState([]);
  const [servicesVideo, setServicesVideo] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/packages/photo`).then(res => {
      setEvents(res.data.events || []);
      setServicesPhoto(res.data.services_photo || []);
      setServicesVideo(res.data.services_video || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const event = events[selectedIdx];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="packages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <span className="text-[#D4AF37] tracking-[0.3em] text-sm uppercase">Pricing</span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#EDEDED] mt-4">
            Our <span className="gold-gradient-text">Packages</span>
          </h1>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">Choose the perfect package for your special occasion. Prices vary by event type.</p>
        </motion.div>

        {/* Discount Notice */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent rounded-xl p-4 mb-8 flex items-center gap-3 border border-[#D4AF37]/20">
          <Info className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
          <p className="text-[#A1A1AA] text-sm">
            <span className="text-[#D4AF37] font-semibold">Spin & Win!</span> Get up to 30% off on orders over £{MINIMUM_DISCOUNT_ORDER}. Spin the wheel on our homepage!
          </p>
        </motion.div>

        {/* Event Type Selector */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-12">
          <h2 className="text-center text-[#A1A1AA] mb-4">Select Event Type to View Pricing:</h2>
          <div className="flex flex-wrap justify-center gap-3" data-testid="event-selector">
            {events.map((evt, idx) => (
              <button key={evt.event_key} onClick={() => setSelectedIdx(idx)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedIdx === idx ? 'gold-gradient-btn' : 'bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED] border border-white/10'
                }`} data-testid={`select-${evt.event_key}`}>
                {evt.name}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Selected Event Info */}
        {event && (
          <motion.div key={event.event_key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-2">{event.name} Packages</h2>
            {event.coverage && <p className="text-[#D4AF37] text-sm">All packages cover: {event.coverage}</p>}
          </motion.div>
        )}

        {/* Package Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16" data-testid="package-cards">
          {['starter', 'classic', 'elite'].map((tier, index) => {
            const pkg = PACKAGE_DETAILS[tier];
            const tierData = event?.tiers?.[tier];
            const isAvailable = !!tierData;
            const isRecommended = event?.recommended === tier;

            return (
              <motion.div key={tier} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className={`package-card relative rounded-2xl p-8 ${
                  !isAvailable ? 'bg-[#0a0a0a]/50 border border-white/5 opacity-60'
                    : pkg.highlight || isRecommended ? 'bg-gradient-to-b from-[#D4AF37]/10 to-[#0a0a0a] border-2 border-[#D4AF37]/50'
                    : 'bg-[#0a0a0a] border border-white/10'
                }`} data-testid={`package-${tier}`}>
                {isAvailable && isRecommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2"><span className="gold-gradient-btn px-4 py-1 rounded-full text-sm">Recommended</span></div>
                )}
                {isAvailable && pkg.highlight && !isRecommended && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2"><span className="gold-gradient-btn px-4 py-1 rounded-full text-sm">Most Popular</span></div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-2">{pkg.name}</h3>
                  <p className="text-[#A1A1AA] text-sm">{tierData?.description || pkg.description}</p>
                </div>
                <div className="text-center mb-6">
                  {isAvailable ? (
                    <>
                      <span className="text-[#D4AF37] text-5xl font-['Playfair_Display'] font-bold">£{tierData.price}</span>
                      <span className="text-[#52525B] text-xs mt-1 block">Delivery: {pkg.deliveryTime}</span>
                    </>
                  ) : (
                    <p className="text-[#52525B]">Not available for this event</p>
                  )}
                </div>
                {isAvailable && (
                  <ul className="space-y-3 mb-8">
                    {(tierData.features || []).map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                        <span className="text-[#A1A1AA] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {isAvailable && (
                  <Link to={`/book?package=${tier}`} data-testid={`book-${tier}-btn`}>
                    <Button className={`w-full rounded-full py-6 ${
                      pkg.highlight || isRecommended ? 'gold-gradient-btn' : 'bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black'
                    }`}>Choose {pkg.name}</Button>
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Additional Photography Services */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
          <div className="text-center mb-8">
            <span className="text-[#D4AF37] tracking-[0.3em] text-sm uppercase flex items-center justify-center gap-2"><Camera className="w-4 h-4" /> Photography Add-ons</span>
            <h2 className="font-['Playfair_Display'] text-3xl text-[#EDEDED] mt-4">Additional <span className="gold-gradient-text">Services</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="photo-services">
            {servicesPhoto.map((service, index) => (
              <motion.div key={service.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-[#0a0a0a] rounded-xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-[#EDEDED]">{service.name}</h3>
                  <span className="text-[#D4AF37] font-['Playfair_Display'] text-2xl">+£{service.price}</span>
                </div>
                <p className="text-[#A1A1AA] text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Videography Services */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
          <div className="text-center mb-8">
            <span className="text-[#D4AF37] tracking-[0.3em] text-sm uppercase flex items-center justify-center gap-2"><Video className="w-4 h-4" /> Videography</span>
            <h2 className="font-['Playfair_Display'] text-3xl text-[#EDEDED] mt-4">Video <span className="gold-gradient-text">Services</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6" data-testid="video-services">
            {servicesVideo.map((service, index) => (
              <motion.div key={service.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="bg-[#0a0a0a] rounded-xl p-6 border border-white/10 hover:border-[#D4AF37]/30 transition-colors">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-[#EDEDED]">{service.name}</h3>
                  <span className="text-[#D4AF37] font-['Playfair_Display'] text-2xl">+£{service.price}</span>
                </div>
                <p className="text-[#A1A1AA] text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What's Included */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="bg-gradient-to-b from-[#D4AF37]/5 to-transparent rounded-2xl p-8 md:p-12 border border-[#D4AF37]/20">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#EDEDED] text-center mb-8">
            What's <span className="gold-gradient-text">Included</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {['Unlimited professionally edited photos', 'High quality colour graded pictures', 'Online delivery via Google Drive or Pendrive', 'High resolution images', 'Professional photography equipment', 'Experienced photographer', 'Multiple location coverage', 'Pre-event consultation'].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-[#A1A1AA]">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Packages;
